import {
  decodePage,
  conditionalRequest,
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requestNoContent,
  requireArray,
  requireBoolean,
  requireNumber,
  requireString,
  requireTimestamp,
  withQuery,
} from '@/lib/api'
import type { AssetsGridValues } from '@/modules/data-grid'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'

type Schemas = components['schemas']

export type AssetReachability = 'reachable' | 'unreachable' | 'stale' | 'unknown'

export function isPrinterDeviceClass(value: string | null | undefined): boolean {
  return value === 'printer' || value === 'network_printer'
}

export function isNetworkDeviceClass(value: string | null | undefined): boolean {
  return value === 'router' || value === 'network_device'
}

export type AssetPosture = AssetsGridValues['posture']

export type AssetInventoryStats = Schemas['AssetInventoryStatsResponse']

export type CanonicalAsset = Replace<
  Schemas['CanonicalAssetDto'],
  'reachability',
  { reachability: AssetReachability }
>

export type PrinterFirmwareValue = Pick<
  Schemas['CanonicalPrinterFirmwareValueDto'],
  'componentId' | 'componentKind' | 'name' | 'physicalIndex' | 'version' | 'conflicting'
> & {
  observation: Pick<Schemas['CanonicalPrinterFirmwareObservationDto'], 'sourceOrdinal' | 'freshness' | 'partial' | 'staleAt'>
}
export type PrinterFirmware = Replace<Schemas['CanonicalPrinterFirmwareDto'], 'values', { values: PrinterFirmwareValue[] }>
export type CanonicalPrinter = Replace<Schemas['CanonicalPrinterDto'], 'firmware', { firmware: PrinterFirmware | null }>
export type CanonicalRouter = Schemas['CanonicalRouterDto']

export type CanonicalComputer = Schemas['CanonicalComputerDto']

export type PrinterSetting = Schemas['PrinterSettingResponse']

export type CanonicalReadState = Schemas['CanonicalReadStateDto']
type CanonicalAssetObservation = Schemas['CanonicalAssetObservationDto']
type CanonicalPrinterMeter = Schemas['CanonicalPrinterMeterDto']
type CanonicalPrinterCounterReading = Schemas['CanonicalPrinterCounterReadingDto']
type CanonicalPrinterCounterIdentity = Schemas['CanonicalPrinterCounterIdentityDto']
type CanonicalPrinterSupply = Schemas['CanonicalPrinterSupplyDto']
export type PrinterSupplyPosition = Pick<CanonicalPrinterSupply, 'positionId' | 'name' | 'type' | 'color' | 'instance' | 'sourceType' | 'sourceColor'>
export type PrinterSupplyConfirmation = {
  state: string | null; installationId: string | null; confirmedAt: string | null; remaining: number | null
}
export type PrinterSupplyCurrentPosition = PrinterSupplyPosition & PrinterSupplyConfirmation & { positionId: string }
export type PrinterSupplyHistoryPoint = Pick<Schemas['PrinterSupplyPointDto'],
  'readingId' | 'positionId' | 'installationId' | 'state' | 'remaining' | 'receivedAt'>
export type PrinterSupplyHistoryPage = Pick<Schemas['PrinterSupplyHistoryDto'], 'nextCursor'> & {
  items: PrinterSupplyHistoryPoint[]; initialPoints: PrinterSupplyHistoryPoint[]
}
type PrinterFormatVocabulary = Schemas['ResourceSettingVocabularyResponse']

function decodeEnum<T extends string>(
  raw: unknown,
  field: string,
  allowed: readonly T[],
): T {
  const value = requireString(raw, field)
  if (allowed.includes(value as T)) return value as T
  throw new Error(`Unexpected ${field} "${value}"`)
}

function decodeCanonicalReadState(raw: unknown): CanonicalReadState {
  if (!isRecord(raw)) throw new Error('Malformed canonical read state')
  return {
    status: requireString(raw.status, 'status'),
    freshness: requireString(raw.freshness, 'freshness'),
    collectedAt: nullableString(raw.collectedAt, 'collectedAt'),
    staleAt: nullableString(raw.staleAt, 'staleAt'),
    requestedIntervalSeconds: nullableString(
      raw.requestedIntervalSeconds,
      'requestedIntervalSeconds',
    ),
    diagnosticCode: nullableString(raw.diagnosticCode, 'diagnosticCode'),
    projectionVersion: requireNumber(raw.projectionVersion, 'projectionVersion'),
    updatedAt: requireString(raw.updatedAt, 'updatedAt'),
  } satisfies CanonicalReadState
}

function decodeCanonicalAssetObservation(raw: unknown): CanonicalAssetObservation {
  if (!isRecord(raw)) throw new Error('Malformed canonical asset observation')
  return {
    agentId: requireString(raw.agentId, 'agentId'),
    agentName: nullableString(raw.agentName, 'agentName'),
    localSubjectId: requireString(raw.localSubjectId, 'localSubjectId'),
    datasetType: requireString(raw.datasetType, 'datasetType'),
    instanceKey: requireString(raw.instanceKey, 'instanceKey'),
    schemaVersion: requireNumber(raw.schemaVersion, 'schemaVersion'),
    revision: requireString(raw.revision, 'revision'),
    collectedAt: requireString(raw.collectedAt, 'collectedAt'),
    receivedAt: requireString(raw.receivedAt, 'receivedAt'),
  } satisfies CanonicalAssetObservation
}

function decodeObservingAgentCount(raw: unknown): number {
  const count = requireNumber(raw, 'observingAgentCount')
  if (!Number.isInteger(count) || count < 0) throw new Error('Malformed observingAgentCount')
  return count
}

function decodeCanonicalPrinter(raw: unknown): CanonicalPrinter {
  if (!isRecord(raw) || !isRecord(raw.summary)
    || (raw.network !== undefined && !isRecord(raw.network))) {
    throw new Error('Malformed canonical printer')
  }
  if (!Array.isArray(raw.observations) || !Array.isArray(raw.counterReadings)
    || !Array.isArray(raw.supplies)) {
    throw new Error('Malformed canonical printer observations')
  }
  const summaryReadState = decodeCanonicalReadState(raw.summary.readState)
  return {
    assetId: requireString(raw.assetId, 'assetId'),
    entityId: requireString(raw.entityId, 'entityId'),
    lifecycleState: requireString(raw.lifecycleState, 'lifecycleState'),
    deviceClass: nullableString(raw.deviceClass, 'deviceClass'),
    summary: {
      readState: summaryReadState,
      name: nullableString(raw.summary.name, 'name'),
      manufacturer: nullableString(raw.summary.manufacturer, 'manufacturer'),
      model: nullableString(raw.summary.model, 'model'),
      serialNumber: nullableString(raw.summary.serialNumber, 'serialNumber'),
      conflicts: requireStringArray(raw.summary.conflicts, 'conflicts'),
    },
    network: raw.network === undefined ? {
      readState: {
        status: 'not_collected', freshness: 'unknown', collectedAt: null, staleAt: null,
        requestedIntervalSeconds: null, diagnosticCode: null, projectionVersion: 4,
        updatedAt: summaryReadState.updatedAt,
      },
      primaryIp: null, primaryMac: null, hostname: null,
    } : {
      readState: decodeCanonicalReadState(raw.network.readState),
      primaryIp: nullableString(raw.network.primaryIp, 'primaryIp'),
      primaryMac: nullableString(raw.network.primaryMac, 'primaryMac'),
      hostname: nullableString(raw.network.hostname, 'hostname'),
    },
    meter: decodeCanonicalPrinterMeter(raw.meter),
    meterReceivedAt: nullableString(raw.meterReceivedAt, 'meterReceivedAt'),
    meterIsLastConfirmed: raw.meterIsLastConfirmed === true,
    counterReadings: raw.counterReadings.map(decodeCanonicalPrinterCounterReading),
    supplies: raw.supplies.map(decodeCanonicalPrinterSupply),
    observingAgentCount: decodeObservingAgentCount(raw.observingAgentCount),
    observations: raw.observations.map(decodeCanonicalAssetObservation),
    firmware: decodePrinterFirmware(raw.firmware),
  } satisfies CanonicalPrinter
}

function decodePrinterFirmware(raw: unknown): PrinterFirmware | null {
  if (raw === undefined || raw === null) return null
  if (!isRecord(raw) || !Array.isArray(raw.values) || raw.values.length > 128) {
    throw new Error('Malformed printer firmware')
  }
  return {
    status: requireString(raw.status, 'status'),
    freshness: requireString(raw.freshness, 'freshness'),
    partial: requireBoolean(raw.partial, 'partial'),
    truncated: requireBoolean(raw.truncated, 'truncated'),
    pending: raw.pending === true,
    values: raw.values.map(decodePrinterFirmwareValue),
  } satisfies PrinterFirmware
}

function decodePrinterFirmwareValue(raw: unknown): PrinterFirmwareValue {
  if (!isRecord(raw) || !isRecord(raw.observation)) throw new Error('Malformed printer firmware value')
  return {
    componentId: requireString(raw.componentId, 'componentId'),
    componentKind: requireString(raw.componentKind, 'componentKind'),
    name: nullableString(raw.name, 'name'),
    physicalIndex: nullableNumber(raw.physicalIndex, 'physicalIndex'),
    version: requireString(raw.version, 'version'),
    conflicting: requireBoolean(raw.conflicting, 'conflicting'),
    observation: {
      sourceOrdinal: requireNumber(raw.observation.sourceOrdinal, 'sourceOrdinal'),
      freshness: requireString(raw.observation.freshness, 'freshness'),
      partial: requireBoolean(raw.observation.partial, 'partial'),
      staleAt: requireTimestamp(raw.observation.staleAt, 'staleAt'),
    },
  } satisfies PrinterFirmwareValue
}

function decodeCanonicalRouter(raw: unknown): CanonicalRouter {
  if (!isRecord(raw) || !isRecord(raw.summary) || (raw.network !== undefined && !isRecord(raw.network))) {
    throw new Error('Malformed canonical router')
  }
  return {
    assetId: requireString(raw.assetId, 'assetId'),
    entityId: requireString(raw.entityId, 'entityId'),
    lifecycleState: requireString(raw.lifecycleState, 'lifecycleState'),
    deviceClass: nullableString(raw.deviceClass, 'deviceClass'),
    network: raw.network === undefined ? {
      readState: { ...decodeCanonicalReadState(raw.summary.readState), status: 'not_collected', freshness: 'unknown', collectedAt: null, staleAt: null },
      managementIp: null, systemName: null, location: null, complete: null, interfaces: [], context: null,
    } : decodeRouterNetwork(raw.network),
    summary: {
      readState: decodeCanonicalReadState(raw.summary.readState),
      name: nullableString(raw.summary.name, 'name'),
      manufacturer: nullableString(raw.summary.manufacturer, 'manufacturer'),
      model: nullableString(raw.summary.model, 'model'),
      serialNumber: nullableString(raw.summary.serialNumber, 'serialNumber'),
      conflicts: requireStringArray(raw.summary.conflicts, 'conflicts'),
    },
  } satisfies CanonicalRouter
}

function decodeRouterNetwork(raw: unknown): CanonicalRouter['network'] {
  if (!isRecord(raw) || !Array.isArray(raw.interfaces) || raw.interfaces.length > 256
    || (raw.complete !== null && typeof raw.complete !== 'boolean')) throw new Error('Malformed router network')
  return {
    readState: decodeCanonicalReadState(raw.readState),
    managementIp: nullableString(raw.managementIp, 'managementIp'),
    systemName: nullableString(raw.systemName, 'systemName'),
    location: nullableString(raw.location, 'location'),
    complete: raw.complete,
    context: decodeRouterContext(raw.context),
    interfaces: raw.interfaces.map((port) => {
      if (!isRecord(port) || !Number.isSafeInteger(port.index) || Number(port.index) <= 0
        || Number(port.index) > 2147483647) throw new Error('Malformed router interface')
      const speed = nullableString(port.speedBps, 'speedBps')
      if (speed !== null && !/^[1-9][0-9]{0,15}$/.test(speed)) throw new Error('Malformed interface speed')
      const addresses = requireStringArray(port.ipAddresses, 'ipAddresses')
      if (addresses.length > 256) throw new Error('Malformed interface addresses')
      return { index: Number(port.index), type: decodeInterfaceInteger(port.type, 'type'), mtu: decodeInterfaceInteger(port.mtu, 'mtu'), name: nullableString(port.name, 'name'),
        description: nullableString(port.description, 'description'), alias: nullableString(port.alias, 'alias'),
        adminStatus: nullableNumber(port.adminStatus, 'adminStatus'), operStatus: nullableNumber(port.operStatus, 'operStatus'),
        speedBps: speed, mac: nullableString(port.mac, 'mac'), ipAddresses: addresses, ipAddressStatuses: decodeRouterAddressStatuses(port.ipAddressStatuses, addresses) }
    }),
  }
}

function decodeInterfaceInteger(raw: unknown, field: string): number | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 1 || raw > 2147483647) throw new Error(`Malformed interface ${field}`)
  return raw
}

function decodeRouterContext(raw: unknown): CanonicalRouter['network']['context'] {
  if (raw === undefined || raw === null) return null
  if (!isRecord(raw) || (raw.wireless !== null && !Array.isArray(raw.wireless)) || (Array.isArray(raw.wireless) && raw.wireless.length > 256)) throw new Error('Malformed network context')
  const boundedText = (value: unknown, field: string) => {
    const text = nullableString(value, field)
    if (text !== null && (text.length > 255 || [...text].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127))) throw new Error(`Malformed ${field}`)
    return text
  }
  const unsigned = (value: unknown, field: string) => {
    if (value === null) return null
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > 4294967295) throw new Error(`Malformed ${field}`)
    return value
  }
  let peerCount = 0
  const indexes = new Set<number>()
  return {
    systemDescription: boundedText(raw.systemDescription, 'systemDescription'),
    uptimeCentiseconds: unsigned(raw.uptimeCentiseconds, 'uptimeCentiseconds'),
    role: boundedText(raw.role, 'role'), roleEvidence: boundedText(raw.roleEvidence, 'roleEvidence'),
    wireless: (raw.wireless ?? []).map((radio: unknown) => {
      if (!isRecord(radio) || !Number.isInteger(radio.index) || Number(radio.index) < 1 || Number(radio.index) > 255
        || !Array.isArray(radio.peers)) throw new Error('Malformed wireless radio')
      if (indexes.has(Number(radio.index))) throw new Error('Duplicate wireless radio')
      indexes.add(Number(radio.index))
      peerCount += radio.peers.length
      if (peerCount > 256) throw new Error('Malformed wireless peers')
      const signal = nullableNumber(radio.signalDbm, 'signalDbm')
      if (signal !== null && (!Number.isInteger(signal) || signal >= 0 || signal < -127)) throw new Error('Malformed wireless signal')
      return { index: Number(radio.index), ssid: boundedText(radio.ssid, 'ssid'), security: boundedText(radio.security, 'security'),
        mode: boundedText(radio.mode, 'mode'), stationCount: unsigned(radio.stationCount, 'stationCount'), signalDbm: signal,
        peers: radio.peers.map(peer => {
          if (!isRecord(peer) || typeof peer.mac !== 'string' || !/^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/.test(peer.mac)) throw new Error('Malformed wireless peer')
          return { mac: peer.mac, name: boundedText(peer.name, 'peer name') }
        }) }
    }),
  }
}

function decodeRouterAddressStatuses(raw: unknown, addresses: string[]): Record<string, number> | null {
  if (raw === undefined || raw === null) return null
  if (!isRecord(raw)) throw new Error('Malformed address statuses')
  const statuses: Record<string, number> = {}
  for (const [address, status] of Object.entries(raw)) {
    if (!addresses.includes(address) || typeof status !== 'number' || !Number.isInteger(status)
      || status < -2147483648 || status > 2147483647) throw new Error('Malformed address status')
    statuses[address] = status
  }
  return statuses
}

function nullableTimestamp(raw: unknown, field: string): string | null {
  return nullableString(raw, field) === null ? null : requireTimestamp(raw, field)
}

function decodeSupplyRemaining(remaining: unknown, field: string): number | null {
  if (remaining !== null && (typeof remaining !== 'number'
    || !Number.isFinite(remaining) || remaining < 0 || remaining > 1)) {
    throw new Error(`Malformed ${field}`)
  }
  return remaining
}

function decodePrinterSupplyHistoryPoint(raw: unknown): PrinterSupplyHistoryPoint {
  if (!isRecord(raw)) throw new Error('Malformed printer supply history point')
  return {
    readingId: requireString(raw.readingId, 'readingId'),
    positionId: requireString(raw.positionId, 'positionId'),
    installationId: nullableString(raw.installationId, 'installationId'),
    state: requireString(raw.state, 'state'),
    remaining: decodeSupplyRemaining(raw.remaining, 'printer supply history remaining'),
    receivedAt: requireTimestamp(raw.receivedAt, 'receivedAt'),
  } satisfies PrinterSupplyHistoryPoint
}

function decodePrinterSupplyHistoryPage(raw: unknown): PrinterSupplyHistoryPage {
  if (!isRecord(raw)) throw new Error('Malformed printer supply history')
  return {
    items: requireArray(raw.items, 'items', decodePrinterSupplyHistoryPoint),
    initialPoints: raw.initialPoints === undefined ? [] : requireArray(raw.initialPoints, 'initialPoints', decodePrinterSupplyHistoryPoint),
    nextCursor: nullableString(raw.nextCursor, 'nextCursor'),
  } satisfies PrinterSupplyHistoryPage
}

function decodeCanonicalPrinterSupply(raw: unknown): CanonicalPrinterSupply {
  if (!isRecord(raw)) throw new Error('Malformed canonical printer supply')
  const remaining = decodeSupplyRemaining(raw.remaining, 'canonical printer supply remaining')
  const instance = raw.instance
  if (instance !== undefined && instance !== null
    && (typeof instance !== 'number' || !Number.isInteger(instance) || instance < 0 || instance > 255)) {
    throw new Error('Malformed canonical printer supply instance')
  }
  return {
    name: requireString(raw.name, 'name'),
    type: requireString(raw.type, 'type'),
    remaining,
    serialNumber: nullableString(raw.serialNumber, 'serialNumber'),
    ...(raw.color !== undefined ? { color: nullableString(raw.color, 'color') } : {}),
    ...(instance !== undefined ? { instance } : {}),
    ...(raw.sourceType !== undefined ? { sourceType: nullableString(raw.sourceType, 'sourceType') } : {}),
    ...(raw.sourceColor !== undefined ? { sourceColor: nullableString(raw.sourceColor, 'sourceColor') } : {}),
    ...(raw.positionId !== undefined ? { positionId: nullableString(raw.positionId, 'positionId') } : {}),
  }
}

function decodeCanonicalPrinterMeter(raw: unknown): CanonicalPrinterMeter | null {
  if (raw === null) return null
  if (!isRecord(raw)) throw new Error('Malformed canonical printer meter')
  const count = requireString(raw.count, 'count')
  const unit = nullableString(raw.unit, 'unit')
  if (!/^(0|[1-9]\d*)$/.test(count) || BigInt(count) > 9_007_199_254_740_992n
    || (unit !== null && unit !== 'sheets' && unit !== 'impressions')) {
    throw new Error('Malformed canonical printer meter')
  }
  return { count, unit }
}

function decodeCanonicalPrinterCounterReading(raw: unknown): CanonicalPrinterCounterReading {
  if (!isRecord(raw)) throw new Error('Malformed canonical printer counter')
  const value = nullableString(raw.value, 'value')
  const isMissing = raw.isMissing
  const isUnobserved = raw.isUnobserved
  if (typeof isMissing !== 'boolean' || typeof isUnobserved !== 'boolean'
    || (value === null) !== (isMissing || isUnobserved)
    || (isMissing && isUnobserved)
    || (value !== null && (!/^(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?$/.test(value)
      || !Number.isFinite(Number(value)) || Number(value) > 9_007_199_254_740_992))) {
    throw new Error('Malformed canonical printer counter')
  }
  return {
    formatId: requireString(raw.formatId, 'formatId'),
    formatName: requireString(raw.formatName, 'formatName'),
    counterId: requireString(raw.counterId, 'counterId'),
    counterName: requireString(raw.counterName, 'counterName'),
    value,
    isMissing,
    isUnobserved,
    ...(raw.identity === undefined ? {} : { identity: decodeCanonicalPrinterCounterIdentity(raw.identity) }),
  } satisfies CanonicalPrinterCounterReading
}

function decodeCanonicalPrinterCounterIdentity(raw: unknown): CanonicalPrinterCounterIdentity | null {
  if (raw === null) return null
  if (!isRecord(raw)) throw new Error('Malformed canonical printer counter identity')
  const variantId = requireNumber(raw.variantId, 'variantId')
  const interpretationRevision = requireNumber(raw.interpretationRevision, 'interpretationRevision')
  if (!Number.isInteger(variantId) || variantId < 1 || variantId > 32767
    || !Number.isInteger(interpretationRevision) || interpretationRevision < 0
    || interpretationRevision > 2147483647 || raw.dimensions === undefined
    || raw.kindId === undefined || raw.unit === undefined) {
    throw new Error('Malformed canonical printer counter identity')
  }
  return {
    kindId: nullableString(raw.kindId, 'kindId'),
    variantId,
    formatId: requireString(raw.formatId, 'formatId'),
    name: requireString(raw.name, 'name'),
    dimensions: raw.dimensions,
    unit: nullableString(raw.unit, 'unit'),
    interpretationRevision,
    mappingState: requireString(raw.mappingState, 'mappingState'),
  } satisfies CanonicalPrinterCounterIdentity
}

function requireStringArray(value: unknown, field: string): string[] {
  if (!Array.isArray(value))
    throw new Error(`Expected an array of strings for "${field}"`)
  return value.map((entry, index) => requireString(entry, `${field}[${index}]`))
}

function decodeAssetInventoryStats(raw: unknown): AssetInventoryStats {
  if (!isRecord(raw)) throw new Error('Malformed asset inventory stats')
  return {
    totalAssets: requireNumber(raw.totalAssets, 'totalAssets'),
    recentlySeenAssets: requireNumber(raw.recentlySeenAssets, 'recentlySeenAssets'),
    reachableAssets: requireNumber(raw.reachableAssets, 'reachableAssets'),
    unreachableAssets: requireNumber(raw.unreachableAssets, 'unreachableAssets'),
    staleAssets: requireNumber(raw.staleAssets, 'staleAssets'),
    unknownAssets: requireNumber(raw.unknownAssets, 'unknownAssets'),
    discoveredDevices: requireNumber(raw.discoveredDevices, 'discoveredDevices'),
    assetsByClass: requireArray(raw.assetsByClass, 'assetsByClass', (item) => {
      if (!isRecord(item)) throw new Error('Malformed asset class count')
      return { deviceClass: requireString(item.deviceClass, 'deviceClass'), count: requireNumber(item.count, 'count') } satisfies Schemas['AssetClassCount']
    }),
  } satisfies AssetInventoryStats
}

function decodeCanonicalAsset(raw: unknown): CanonicalAsset {
  if (!isRecord(raw)) throw new Error('Malformed canonical asset')
  return {
    assetId: requireString(raw.assetId, 'assetId'),
    name: requireString(raw.name, 'name'),
    customName: nullableString(raw.customName, 'customName'),
    deviceClass: nullableString(raw.deviceClass, 'deviceClass'),
    manufacturer: nullableString(raw.manufacturer, 'manufacturer'),
    model: nullableString(raw.model, 'model'),
    reachability: decodeEnum(raw.reachability, 'reachability', [
      'reachable',
      'unreachable',
      'stale',
      'unknown',
    ]),
    lastSeenAt: nullableString(raw.lastSeenAt, 'lastSeenAt'),
    lastKnownNameAt: nullableString(raw.lastKnownNameAt, 'lastKnownNameAt'),
    lastKnownManufacturerAt: nullableString(raw.lastKnownManufacturerAt, 'lastKnownManufacturerAt'),
    lastKnownModelAt: nullableString(raw.lastKnownModelAt, 'lastKnownModelAt'),
    serialNumber: nullableString(raw.serialNumber, 'serialNumber'),
    lastKnownSerialAt: nullableString(raw.lastKnownSerialAt, 'lastKnownSerialAt'),
    createdAt: requireString(raw.createdAt, 'createdAt'),
  } satisfies CanonicalAsset
}

function decodePrinterSetting(raw: unknown): PrinterSetting {
  if (!isRecord(raw)) throw new Error('Malformed printer setting')
  return {
    resourceId: requireString(raw.resourceId, 'resourceId'),
    entityId: requireString(raw.entityId, 'entityId'),
    resourceType: decodeEnum(raw.resourceType, 'resourceType', ['printer']),
    key: requireString(raw.key, 'key'),
    value: raw.value ?? null,
    isOverride: requireBoolean(raw.isOverride, 'isOverride'),
    source: decodeEnum(raw.source, 'source', ['resource', 'entity', 'default', 'unresolved']),
    sourceEntityId: nullableString(raw.sourceEntityId, 'sourceEntityId'),
    inheritedDistance: nullableNumber(raw.inheritedDistance, 'inheritedDistance'),
  } satisfies PrinterSetting
}

function decodePrinterSettings(raw: unknown): PrinterSetting[] {
  if (!isRecord(raw)) throw new Error('Malformed printer settings response')
  return requireArray(raw.items, 'printer settings', decodePrinterSetting)
}

function requireStrings(raw: unknown, name: string): string[] {
  return requireArray(raw, name, (item) => requireString(item, name))
}

function decodeCanonicalSummary(raw: unknown): Schemas['CanonicalComputerSummaryDto'] {
  if (!isRecord(raw)) throw new Error('Malformed canonical computer summary')
  const lastConfirmedAt = raw.lastConfirmedAt ?? {}
  if (!isRecord(lastConfirmedAt)) throw new Error('Malformed last confirmed System fields')
  return {
    readState: decodeCanonicalReadState(raw.readState), hostname: nullableString(raw.hostname, 'hostname'),
    manufacturer: nullableString(raw.manufacturer, 'manufacturer'), model: nullableString(raw.model, 'model'),
    serialNumber: nullableString(raw.serialNumber, 'serialNumber'), chassis: nullableString(raw.chassis, 'chassis'),
    architecture: nullableString(raw.architecture, 'architecture'), cpuModel: nullableString(raw.cpuModel, 'cpuModel'),
    cpuSpeedHz: nullableNumber(raw.cpuSpeedHz, 'cpuSpeedHz'), physicalCores: nullableNumber(raw.physicalCores, 'physicalCores'),
    logicalCores: nullableNumber(raw.logicalCores, 'logicalCores'), totalRamBytes: nullableNumber(raw.totalRamBytes, 'totalRamBytes'),
    biosManufacturer: nullableString(raw.biosManufacturer, 'biosManufacturer'), biosVersion: nullableString(raw.biosVersion, 'biosVersion'),
    biosReleaseDate: nullableString(raw.biosReleaseDate, 'biosReleaseDate'),
    osEdition: nullableString(raw.osEdition, 'osEdition'), osVersion: nullableString(raw.osVersion, 'osVersion'),
    osBuild: nullableString(raw.osBuild, 'osBuild'), conflicts: requireStrings(raw.conflicts, 'conflicts'),
    lastConfirmedAt: Object.fromEntries(Object.entries(lastConfirmedAt).map(([field, timestamp]) =>
      [field, requireTimestamp(timestamp, `lastConfirmedAt.${field}`)])),
  }
}

function decodeCanonicalNetwork(raw: unknown): Schemas['CanonicalComputerNetworkDto'] {
  if (!isRecord(raw)) throw new Error('Malformed canonical computer network')
  return {
    readState: decodeCanonicalReadState(raw.readState),
    primaryInterface: nullableString(raw.primaryInterface, 'primaryInterface'),
    primaryIp: nullableString(raw.primaryIp, 'primaryIp'),
    primaryMac: nullableString(raw.primaryMac, 'primaryMac'),
  }
}

function decodeInstalledApplications(raw: unknown): Schemas['CanonicalInstalledApplicationsDto'] {
  if (!isRecord(raw)) throw new Error('Malformed canonical installed applications')
  return { readState: decodeCanonicalReadState(raw.readState), items: requireArray(raw.items, 'installed applications', (item) => {
    if (!isRecord(item)) throw new Error('Malformed installed application')
    return {
      id: requireString(item.id, 'id'), identityKind: requireString(item.identityKind, 'identityKind'),
      name: requireString(item.name, 'name'), publisher: requireString(item.publisher, 'publisher'),
      version: requireString(item.version, 'version'), architecture: requireString(item.architecture, 'architecture'),
      sources: requireStrings(item.sources, 'sources'), scopes: requireStrings(item.scopes, 'scopes'),
      installDates: requireStrings(item.installDates, 'installDates'), installLocations: requireStrings(item.installLocations, 'installLocations'),
    }
  }) }
}

function decodeEndpointProducts(raw: unknown): Schemas['CanonicalEndpointProductsDto'] {
  if (!isRecord(raw)) throw new Error('Malformed canonical endpoint products')
  return {
    sources: requireArray(raw.sources, 'endpoint product sources', (source) => {
      if (!isRecord(source)) throw new Error('Malformed endpoint product source')
      return { classifierKind: requireString(source.classifierKind, 'classifierKind'), agentId: nullableString(source.agentId, 'agentId'), localSubjectId: nullableString(source.localSubjectId, 'localSubjectId'), readState: decodeCanonicalReadState(source.readState), classifierVersion: nullableString(source.classifierVersion, 'classifierVersion') }
    }),
    items: requireArray(raw.items, 'endpoint products', (item) => {
      if (!isRecord(item)) throw new Error('Malformed endpoint product')
      return {
        id: requireString(item.id, 'id'), classifierKind: requireString(item.classifierKind, 'classifierKind'), agentId: nullableString(item.agentId, 'agentId'), localSubjectId: nullableString(item.localSubjectId, 'localSubjectId'),
        name: requireString(item.name, 'name'), category: requireString(item.category, 'category'), version: nullableString(item.version, 'version'),
        installed: requireBoolean(item.installed, 'installed'), running: requireBoolean(item.running, 'running'), registered: requireBoolean(item.registered, 'registered'), confidence: requireString(item.confidence, 'confidence'), classifierVersion: nullableString(item.classifierVersion, 'classifierVersion'),
      }
    }),
  }
}

function decodeFirewall(raw: unknown): Schemas['CanonicalFirewallDto'] {
  if (!isRecord(raw)) throw new Error('Malformed firewall')
  return {
    activeProfileTypesRaw: nullableString(raw.activeProfileTypesRaw, 'activeProfileTypesRaw'),
    localPolicyModifyState: requireString(raw.localPolicyModifyState, 'localPolicyModifyState'),
    localPolicyModifyStateRaw: nullableString(raw.localPolicyModifyStateRaw, 'localPolicyModifyStateRaw'),
    profiles: requireArray(raw.profiles, 'firewall profiles', (profile) => {
      if (!isRecord(profile)) throw new Error('Malformed firewall profile')
      return { name: requireString(profile.name, 'name'), enabled: requireBoolean(profile.enabled, 'enabled'), active: requireBoolean(profile.active, 'active'), blockAllInbound: requireBoolean(profile.blockAllInbound, 'blockAllInbound'), defaultInboundAction: requireString(profile.defaultInboundAction, 'defaultInboundAction'), defaultInboundActionRaw: nullableString(profile.defaultInboundActionRaw, 'defaultInboundActionRaw'), defaultOutboundAction: requireString(profile.defaultOutboundAction, 'defaultOutboundAction'), defaultOutboundActionRaw: nullableString(profile.defaultOutboundActionRaw, 'defaultOutboundActionRaw') } satisfies Schemas['CanonicalFirewallProfileDto']
    }),
  } satisfies Schemas['CanonicalFirewallDto']
}

function decodePosture(raw: unknown): Schemas['CanonicalComputerPostureDto'] {
  if (!isRecord(raw)) throw new Error('Malformed canonical posture')
  return {
    kind: requireString(raw.kind, 'kind'), datasetType: requireString(raw.datasetType, 'datasetType'), readState: decodeCanonicalReadState(raw.readState),
    firewall: raw.firewall == null ? null : decodeFirewall(raw.firewall),
    legacyComponents: requireArray(raw.legacyComponents ?? [], 'legacy components', (component) => {
      if (!isRecord(component)) throw new Error('Malformed legacy component')
      return { name: requireString(component.name, 'name'), state: requireString(component.state, 'state'), evidenceSource: nullableString(component.evidenceSource, 'evidenceSource'), configured: nullableString(component.configured, 'configured'), effective: nullableString(component.effective, 'effective') } satisfies Schemas['CanonicalLegacyComponentDto']
    }),
    netbiosAdapters: requireArray(raw.netbiosAdapters ?? [], 'NetBIOS adapters', (adapter) => {
      if (!isRecord(adapter)) throw new Error('Malformed NetBIOS adapter')
      return { settingId: requireString(adapter.settingId, 'settingId'), ipEnabled: requireBoolean(adapter.ipEnabled, 'ipEnabled'), configured: requireString(adapter.configured, 'configured') } satisfies Schemas['CanonicalNetBIOSAdapterDto']
    }),
    controls: requireArray(raw.controls, 'posture controls', (control) => {
      if (!isRecord(control)) throw new Error('Malformed posture control')
      return { key: requireString(control.key, 'key'), boolean: control.boolean === null ? null : requireBoolean(control.boolean, 'boolean'), integer: nullableString(control.integer, 'integer'), unsignedInteger: nullableString(control.unsignedInteger, 'unsignedInteger'), text: nullableString(control.text, 'text'), textValues: control.textValues === null ? null : requireStrings(control.textValues, 'textValues') }
    }),
    diskEncryptionVolumes: requireArray(raw.diskEncryptionVolumes, 'disk encryption volumes', (volume) => {
      if (!isRecord(volume)) throw new Error('Malformed disk encryption volume')
      return { volumeId: requireString(volume.volumeId, 'volumeId'), driveLetter: requireString(volume.driveLetter, 'driveLetter'), volumeType: requireString(volume.volumeType, 'volumeType'), conversionState: requireString(volume.conversionState, 'conversionState'), encryptionPercentage: volume.encryptionPercentage === null ? null : requireNumber(volume.encryptionPercentage, 'encryptionPercentage'), protectionState: requireString(volume.protectionState, 'protectionState'), encryptionMethod: requireString(volume.encryptionMethod, 'encryptionMethod'), lockState: requireString(volume.lockState, 'lockState'), keyProtectorTypes: requireStrings(volume.keyProtectorTypes, 'keyProtectorTypes') }
    }),
    localAdministrators: requireArray(raw.localAdministrators, 'local administrators', (admin) => {
      if (!isRecord(admin)) throw new Error('Malformed local administrator')
      return { memberSid: requireString(admin.memberSid, 'memberSid'), resolution: requireString(admin.resolution, 'resolution'), sidType: requireString(admin.sidType, 'sidType'), accountName: requireString(admin.accountName, 'accountName'), domainName: requireString(admin.domainName, 'domainName') }
    }),
    missingUpdates: requireArray(raw.missingUpdates, 'missing updates', (update) => {
      if (!isRecord(update)) throw new Error('Malformed missing update')
      return { updateKey: requireString(update.updateKey, 'updateKey'), updateId: requireString(update.updateId, 'updateId'), revision: requireNumber(update.revision, 'revision'), title: requireString(update.title, 'title'), severity: requireString(update.severity, 'severity'), rebootBehavior: requireString(update.rebootBehavior, 'rebootBehavior'), kbIds: requireStrings(update.kbIds, 'kbIds'), categoryIds: requireStrings(update.categoryIds, 'categoryIds'), classificationIds: requireStrings(update.classificationIds, 'classificationIds'), supersedesUpdateIds: requireStrings(update.supersedesUpdateIds, 'supersedesUpdateIds') }
    }),
  }
}

export type PrinterCounterKind = Schemas['PrinterCounterKindDto']

function decodePrinterCounterKind(raw: unknown): PrinterCounterKind {
  if (!isRecord(raw)) throw new Error('Malformed printer counter kind')
  return {
    kindId: requireString(raw.kindId, 'kindId'),
    name: requireString(raw.name, 'name'),
    group: requireString(raw.group, 'group'),
  } satisfies PrinterCounterKind
}

function decodePrinterFormatVocabulary(raw: unknown): PrinterFormatVocabulary {
  if (!isRecord(raw)) throw new Error('Malformed printer format vocabulary')
  return {
    items: requireArray(raw.items, 'printer formats', (item) => {
      if (!isRecord(item)) throw new Error('Malformed printer format')
      return {
        id: requireString(item.id, 'id'),
        label: requireString(item.label, 'label'),
      } satisfies Schemas['ResourceSettingVocabularyItemResponse']
    }),
  } satisfies PrinterFormatVocabulary
}

function decodeCanonicalComputer(raw: unknown): CanonicalComputer {
  if (!isRecord(raw)) throw new Error('Malformed canonical computer')
  return {
    assetId: requireString(raw.assetId, 'assetId'), entityId: requireString(raw.entityId, 'entityId'), lifecycleState: requireString(raw.lifecycleState, 'lifecycleState'), deviceClass: nullableString(raw.deviceClass, 'deviceClass'),
    observingAgentCount: decodeObservingAgentCount(raw.observingAgentCount),
    summary: decodeCanonicalSummary(raw.summary),
    network: decodeCanonicalNetwork(raw.network),
    installedApplications: decodeInstalledApplications(raw.installedApplications),
    endpointProducts: decodeEndpointProducts(raw.endpointProducts), posture: requireArray(raw.posture, 'posture', decodePosture), observations: requireArray(raw.observations, 'observations', decodeCanonicalAssetObservation),
  }
}

export const assetsApi = {
  getPrinterSupplyPositions: (entityId: string, assetId: string) => request(
    `/api/v1/entities/${entityId}/assets/${assetId}/printer/supplies`,
    (raw): PrinterSupplyCurrentPosition[] => {
      if (!isRecord(raw)) throw new Error('Malformed printer supplies')
      return requireArray(raw.positions, 'positions', (position) => {
        if (!isRecord(position)) throw new Error('Malformed printer supply position')
        return {
          positionId: requireString(position.positionId, 'positionId'),
          name: requireString(position.name, 'name'),
          type: requireString(position.type, 'type'),
          color: nullableString(position.color, 'color'),
          instance: requireNumber(position.instance, 'instance'),
          sourceType: nullableString(position.sourceType, 'sourceType'),
          sourceColor: nullableString(position.sourceColor, 'sourceColor'),
          state: nullableString(position.state, 'state'),
          installationId: nullableString(position.installationId, 'installationId'),
          confirmedAt: nullableTimestamp(position.confirmedAt, 'confirmedAt'),
          remaining: decodeSupplyRemaining(position.remaining ?? null, 'printer supply position remaining'),
        }
      })
    },
  ),
  getPrinterSupplyHistory: (
    entityId: string,
    assetId: string,
    query: { from: string; to: string; positionId: string; after?: string },
    init?: RequestInit,
  ) => request(
    withQuery(`/api/v1/entities/${entityId}/assets/${assetId}/printer/supplies/history`, { ...query, limit: '200' }),
    decodePrinterSupplyHistoryPage,
    init,
  ),
  getCanonicalComputer: (entityId: string, assetId: string) => conditionalRequest(
    `/api/v1/entities/${entityId}/assets/${assetId}/computer`, decodeCanonicalComputer,
  ),
  getAsset: (entityId: string, assetId: string) =>
    request(
      `/api/v1/entities/${entityId}/assets/${assetId}`,
      decodeCanonicalAsset,
    ),
  setName: (entityId: string, assetId: string, name: string | null) =>
    requestNoContent(`/api/v1/entities/${entityId}/assets/${assetId}/name`, json('PUT', { name })),
  getPrinter: (entityId: string, assetId: string) =>
    request(
      `/api/v1/entities/${entityId}/assets/${assetId}/printer`,
      decodeCanonicalPrinter,
    ),
  getRouter: (entityId: string, assetId: string) =>
    request(
      `/api/v1/entities/${entityId}/assets/${assetId}/router`,
      decodeCanonicalRouter,
    ),
  getRouterSources: (entityId: string, assetId: string, after?: string) => {
    const query = new URLSearchParams()
    if (after) query.set('after', after)
    return request(
      `/api/v1/entities/${entityId}/assets/${assetId}/router/sources${query.size ? `?${query}` : ''}`,
      (raw) => decodePage(raw, decodeCanonicalAssetObservation, 'network device observations'),
    )
  },
  getPrinterSettings: (entityId: string, assetId: string) =>
    request(
      `/api/v1/entities/${entityId}/assets/${assetId}/printer/settings`,
      decodePrinterSettings,
    ),
  getStats: (entityId: string, includeChildren: boolean) =>
    request(
      `/api/v1/entities/${entityId}/assets/stats${includeChildren ? '' : '?includeDescendants=false'}`,
      decodeAssetInventoryStats,
    ),
  getPrinterFormats: (entityId: string) =>
    request(
      `/api/v1/entities/${entityId}/resource-settings/printer/vocabularies/formats`,
      decodePrinterFormatVocabulary,
    ),
  getPrinterCounterKinds: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/print/counter-kinds`, (raw) =>
      requireArray(raw, 'printer counter kinds', decodePrinterCounterKind),
    ),
}
